// ---------------------------------------------------------------------------
// Zera Travel — Social Media Hub Academy
// Shared types for the interactive training presentation + certification quiz.
// Single source of truth: every slide, block and quiz question conforms here.
// ---------------------------------------------------------------------------

export type CalloutTone = 'tip' | 'warn' | 'info' | 'gold';

export type SlideBlock =
  | { type: 'lead'; text: string }
  | { type: 'bullets'; title?: string; items: string[] }
  | { type: 'steps'; title?: string; items: { title: string; desc: string }[] }
  | { type: 'callout'; tone: CalloutTone; title: string; text: string }
  | { type: 'dodont'; dos: string[]; donts: string[] }
  // Interactive: expandable hotspot panels the learner clicks to reveal detail
  | { type: 'hotspots'; title?: string; items: { label: string; detail: string }[] }
  // Visual pipeline (e.g. approval workflow states)
  | {
      type: 'workflow';
      title?: string;
      stages: { label: string; desc: string; tone?: 'gold' | 'green' | 'red' | 'blue' | 'neutral' }[];
    }
  // Inline knowledge check (instant feedback, not graded)
  | { type: 'check'; question: string; choices: string[]; correctIndex: number; explain: string }
  | { type: 'faq'; items: { q: string; a: string }[] };

export interface TrainingSlide {
  id: string;
  title: string;
  kicker?: string;
  blocks: SlideBlock[];
  /** Presenter script — shown in the collapsible Speaker Notes drawer. */
  notes: string;
  /** Rendered as a gold "Key takeaways" panel at the bottom of the slide. */
  takeaways?: string[];
}

export interface TrainingSection {
  id: string;
  num: number;
  title: string;
  /** Short label used in the section navigator. */
  short: string;
  /** lucide icon key, mapped inside the player. */
  icon: string;
  /** Estimated minutes to complete. */
  minutes: number;
  intro: string;
  objectives: string[];
  slides: TrainingSlide[];
}

export type QuizDifficulty = 'easy' | 'medium' | 'advanced';

export interface QuizQuestion {
  id: string;
  /** Section id this question maps back to (used for topic reporting + review links). */
  topic: string;
  topicLabel: string;
  difficulty: QuizDifficulty;
  q: string;
  /** Choice index 0 is ALWAYS the correct answer in the bank; shuffled at runtime. */
  choices: string[];
  explain: string;
}

// ---------------------------------------------------------------------------
// Progress / reporting types (persisted per admin realm in localStorage)
// ---------------------------------------------------------------------------

export interface QuizAnswerRecord {
  qid: string;
  topic: string;
  topicLabel: string;
  question: string;
  chosen: string;
  correct: string;
  explain: string;
  wasCorrect: boolean;
  difficulty: QuizDifficulty;
}

export interface QuizAttempt {
  id: string;
  dateISO: string;
  scorePct: number;
  correctCount: number;
  total: number;
  passed: boolean;
  durationSec: number;
  answers: QuizAnswerRecord[];
}

export interface TrainingProgress {
  /** slideId -> true for every slide the learner has viewed. */
  visited: Record<string, true>;
  completedSections: string[];
  skippedSections: string[];
  bookmarks: string[];
  lastPosition: { sectionIdx: number; slideIdx: number } | null;
  timeSpentSec: number;
  attempts: QuizAttempt[];
  certified: boolean;
  certifiedAt?: string;
  certifiedName?: string;
}

export const PASSING_SCORE = 80;
export const QUIZ_SIZE = 10;
